import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Filter, X, Search, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { apiFilter, apiAuth } from '../api/client';
import { useFilterStore } from '../hooks/useFilterStore';
import SavedViewsControl from './SavedViewsControl';

/**
 * Sprint 8 — FilterBar (pattern Float — cf. analyse/float-filter-analysis.md).
 *
 * Faceted filter UI :
 *  - Bouton « Filter » dans la toolbar de chaque vue.
 *  - Chips horizontales pour les conditions actives (édition au clic, ✕ pour
 *    supprimer).
 *  - Bouton « Effacer » global qui apparaît dès qu'une condition est active.
 *  - Popover en 2 niveaux : choix de dimension → sélection des valeurs avec
 *    toggle « is / is not » et search-within-picker.
 *
 * État géré par `useFilterStore(scope)` (localStorage persist). Les valeurs
 * entity_ref sont fetchées via `/api/filter/values/:dimension` ; les valeurs
 * statiques (enum) sont fournies inline dans la métadonnée des dimensions.
 *
 * Props :
 *  - scope     : 'schedule' | 'affaires' | 'persons' | 'logtime' | 'timeoffs'
 *  - context?  : { affaireId?, countryCode? } — propagé aux endpoints values
 *                pour les dimensions contextuelles (ex: phase requiert affaireId).
 */
// Cache module-level : évite un fetch /auth/me en double si plusieurs
// FilterBar sont montés simultanément.
let _mePromise = null;
function fetchMe() {
  if (!_mePromise) {
    _mePromise = apiAuth.me().catch(() => null);
  }
  return _mePromise;
}

export default function FilterBar({ scope, context = null }) {
  const { conditions, addCondition, updateCondition, removeCondition, clearAll } =
    useFilterStore(scope);

  // Métadonnée des dimensions du scope (chargée une fois au mount).
  const [dimensions, setDimensions] = useState([]);
  // Cache des libellés `{[dimension]: {[valueId]: label}}` pour rendre les chips.
  const [labelCache, setLabelCache] = useState({});
  // User courant (pour le raccourci "Moi" — résolu via /auth/me.personId).
  const [me, setMe] = useState(null);

  // État du popover : null = fermé. Sinon { mode: 'dimension'|'values',
  //   anchor, conditionId? (édition), dimensionKey? (drill-down) }
  const [popover, setPopover] = useState(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    apiFilter
      .dimensions(scope)
      .then((res) => {
        if (cancelled) return;
        setDimensions(res.dimensions ?? []);
        // Pré-charge les libellés statiques dans le cache.
        const seed = {};
        for (const d of res.dimensions ?? []) {
          if (d.valueSource === 'static' && Array.isArray(d.staticValues)) {
            seed[d.key] = {};
            for (const v of d.staticValues) {
              seed[d.key][String(v.id)] = v.label;
            }
          }
        }
        if (Object.keys(seed).length > 0) {
          setLabelCache((prev) => ({ ...prev, ...seed }));
        }
      })
      .catch(() => {
        if (!cancelled) setDimensions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [scope]);

  // Récupère me() pour activer le bouton "Moi".
  useEffect(() => {
    let cancelled = false;
    fetchMe().then((resp) => {
      if (cancelled) return;
      const u = resp?.user ?? null;
      setMe(u);
      // Pré-remplit le labelCache pour que le chip affiche "<Mon Nom>" plutôt
      // que le cuid quand le filtre "Moi" est appliqué.
      if (u?.personId && u?.personName) {
        setLabelCache((prev) => ({
          ...prev,
          person: { ...(prev.person ?? {}), [String(u.personId)]: u.personName },
        }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Helper : enregistre un label dans le cache (appelé par le ValuePicker).
  const registerLabels = useCallback((dimensionKey, items) => {
    setLabelCache((prev) => {
      const next = { ...prev, [dimensionKey]: { ...(prev[dimensionKey] ?? {}) } };
      for (const it of items) {
        next[dimensionKey][String(it.id)] = it.label;
      }
      return next;
    });
  }, []);

  const dimensionByKey = useMemo(() => {
    const m = {};
    for (const d of dimensions) m[d.key] = d;
    return m;
  }, [dimensions]);

  // Hydrate les labels des conditions persistées (localStorage) dont les
  // valeurs entity_ref n'ont pas encore de label en cache. Sans ça, après un
  // hard reset, les chips affichent les cuids bruts (ex: "cmpobbnj...").
  // Groupe par dimension et fait un POST de type GET avec ?ids=cuid1,cuid2.
  useEffect(() => {
    if (dimensions.length === 0 || conditions.length === 0) return undefined;
    let cancelled = false;
    const missingByDim = new Map();
    for (const cond of conditions) {
      const meta = dimensionByKey[cond.dimension];
      if (!meta || meta.valueSource !== 'endpoint') continue;
      const cache = labelCache[cond.dimension] ?? {};
      for (const v of cond.values) {
        const key = String(v);
        if (!cache[key]) {
          if (!missingByDim.has(cond.dimension)) missingByDim.set(cond.dimension, new Set());
          missingByDim.get(cond.dimension).add(key);
        }
      }
    }
    if (missingByDim.size === 0) return undefined;
    Promise.all(
      Array.from(missingByDim.entries()).map(([dim, ids]) =>
        apiFilter
          .values(dim, { ids: Array.from(ids) })
          .then((res) => ({ dim, items: Array.isArray(res?.items) ? res.items : [] }))
          .catch(() => ({ dim, items: [] })),
      ),
    ).then((results) => {
      if (cancelled) return;
      setLabelCache((prev) => {
        const next = { ...prev };
        for (const { dim, items } of results) {
          if (items.length === 0) continue;
          next[dim] = { ...(next[dim] ?? {}) };
          for (const it of items) {
            next[dim][String(it.id)] = it.label;
          }
        }
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [dimensions, conditions, dimensionByKey, labelCache]);

  // Raccourci "Moi" — ticket #36 : c'est un **toggle**, pas un simple bouton
  // d'ajout. Avant, il disparaissait dès qu'il était cliqué (remplacé par une
  // chip « Personne est <mon nom> ») : impossible de revenir en arrière depuis
  // le raccourci, ce qui se lisait comme « le filtre Moi ne se supprime pas ».
  // Désormais il reste affiché, s'allume quand la condition correspondante est
  // active, et un second clic la retire.
  const hasPersonDimension = Boolean(dimensionByKey.person);
  // Condition posée par le raccourci : `person is [monPersonId]` exactement.
  const meCondition = useMemo(() => {
    if (!me?.personId) return null;
    return (
      conditions.find(
        (c) =>
          c.dimension === 'person' &&
          c.operator === 'is' &&
          c.values.length === 1 &&
          String(c.values[0]) === String(me.personId),
      ) ?? null
    );
  }, [conditions, me?.personId]);
  // Une autre condition `person` (autre personne, ou `n'est pas`) rendrait le
  // toggle ambigu — et deux conditions `person` en AND ne remontent rien. Dans
  // ce cas on masque le raccourci, l'utilisateur pilote via les chips.
  const hasOtherPersonCondition = conditions.some(
    (c) => c.dimension === 'person' && c.id !== meCondition?.id,
  );
  const canShowMeShortcut =
    hasPersonDimension && Boolean(me?.personId) && !hasOtherPersonCondition;
  const meActive = Boolean(meCondition);

  function toggleMeFilter() {
    if (!me?.personId) return;
    if (meCondition) {
      removeCondition(meCondition.id);
      return;
    }
    addCondition({ dimension: 'person', operator: 'is', values: [me.personId] });
  }

  function openDimensionPicker() {
    const rect = triggerRef.current?.getBoundingClientRect();
    setPopover({ mode: 'dimension', anchor: rect, conditionId: null });
  }

  function openValuePickerForNew(dimensionKey) {
    const rect = triggerRef.current?.getBoundingClientRect();
    setPopover({ mode: 'values', anchor: rect, conditionId: null, dimensionKey });
  }

  function openValuePickerForEdit(condition) {
    const rect = triggerRef.current?.getBoundingClientRect();
    setPopover({
      mode: 'values',
      anchor: rect,
      conditionId: condition.id,
      dimensionKey: condition.dimension,
    });
  }

  function closePopover() {
    setPopover(null);
  }

  // Ticket #78 — valeur choisie depuis la recherche globale : on l'ajoute à la
  // condition `est` déjà posée sur cette dimension (OR entre valeurs), sinon on
  // en crée une. Deux conditions sur la même dimension seraient en AND et ne
  // remonteraient rien.
  function addValueFromSearch(dimensionKey, item) {
    registerLabels(dimensionKey, [item]);
    const existing = conditions.find(
      (c) => c.dimension === dimensionKey && c.operator === 'is',
    );
    if (existing) {
      if (!existing.values.some((v) => sameValue(v, item.id))) {
        updateCondition(existing.id, {
          operator: 'is',
          values: [...existing.values, item.id],
        });
      }
    } else {
      addCondition({ dimension: dimensionKey, operator: 'is', values: [item.id] });
    }
    closePopover();
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 6,
        // Ticket #34 — sans minWidth: 0, une chip au libellé long ne peut pas
        // rétrécir et déborde sur les contrôles voisins (navigation de période).
        minWidth: 0,
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={openDimensionPicker}
        title="Ajouter un filtre"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          background: 'transparent',
          color: 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        <Filter size={14} />
        Filtrer
      </button>

      {canShowMeShortcut && (
        <button
          type="button"
          onClick={toggleMeFilter}
          aria-pressed={meActive}
          title={
            meActive
              ? `Retirer le filtre sur ${me.personName}`
              : `Filtrer sur ${me.personName}`
          }
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            background: meActive ? 'var(--primary)' : 'var(--glass)',
            color: meActive ? '#fff' : 'var(--text)',
            border: `1px solid ${meActive ? 'var(--primary)' : 'var(--glass-border)'}`,
            borderRadius: 'var(--radius-pill)',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {meActive ? <X size={14} /> : <User size={14} />}
          Moi
        </button>
      )}

      {/* Ticket #36 — la condition portée par le toggle « Moi » n'est pas
          dupliquée en chip : le pill allumé la représente déjà. */}
      {conditions
        .filter((cond) => !(canShowMeShortcut && cond.id === meCondition?.id))
        .map((cond) => (
        <FilterChip
          key={cond.id}
          condition={cond}
          dimensionMeta={dimensionByKey[cond.dimension]}
          labelCache={labelCache[cond.dimension] ?? {}}
          onEdit={() => openValuePickerForEdit(cond)}
          onRemove={() => removeCondition(cond.id)}
        />
      ))}

      {/* Ticket #37 — vues enregistrées (persistées serveur, par utilisateur). */}
      <SavedViewsControl scope={scope} />

      {conditions.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          style={{
            padding: '4px 10px',
            background: 'transparent',
            color: 'var(--text-muted)',
            border: 'none',
            fontSize: 12,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Effacer
        </button>
      )}

      {popover &&
        ReactDOM.createPortal(
          <PopoverShell anchor={popover.anchor} onClose={closePopover}>
            {popover.mode === 'dimension' && (
              <DimensionList
                scope={scope}
                context={context}
                dimensions={dimensions}
                onPick={(dim) => openValuePickerForNew(dim.key)}
                onPickValue={addValueFromSearch}
              />
            )}
            {popover.mode === 'values' && (
              <ValuePicker
                dimension={dimensionByKey[popover.dimensionKey]}
                context={context}
                editingCondition={
                  popover.conditionId
                    ? conditions.find((c) => c.id === popover.conditionId) ?? null
                    : null
                }
                onLabelsLoaded={(items) =>
                  registerLabels(popover.dimensionKey, items)
                }
                onBack={() =>
                  setPopover({ mode: 'dimension', anchor: popover.anchor })
                }
                onCommit={(operator, values) => {
                  if (popover.conditionId) {
                    if (values.length === 0) {
                      removeCondition(popover.conditionId);
                    } else {
                      updateCondition(popover.conditionId, { operator, values });
                    }
                  } else if (values.length > 0) {
                    addCondition({
                      dimension: popover.dimensionKey,
                      operator,
                      values,
                    });
                  }
                  closePopover();
                }}
              />
            )}
          </PopoverShell>,
          document.body,
        )}
    </div>
  );
}

// ---------- FilterChip ----------

function FilterChip({ condition, dimensionMeta, labelCache, onEdit, onRemove }) {
  const opLabel = condition.operator === 'is_not' ? 'n’est pas' : 'est';
  const dimLabel = dimensionMeta?.label ?? condition.dimension;

  let valueLabel;
  if (condition.values.length === 1) {
    const v = condition.values[0];
    valueLabel = labelCache[String(v)] ?? (typeof v === 'boolean' ? (v ? 'Oui' : 'Non') : String(v));
  } else {
    valueLabel = `${condition.values.length} valeurs`;
  }

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 4px 4px 10px',
        background: 'var(--glass)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-pill)',
        fontSize: 12,
        color: 'var(--text)',
        // Ticket #34 — borne la largeur d'une chip au libellé long (nom
        // d'affaire complet) : elle se tronque au lieu de déborder.
        maxWidth: 320,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        onClick={onEdit}
        title={`${dimLabel} ${opLabel} ${valueLabel}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: 'transparent',
          color: 'var(--text)',
          border: 'none',
          padding: 0,
          fontSize: 12,
          cursor: 'pointer',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{dimLabel}</span>
        <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{opLabel}</span>
        {/* Seule la valeur est tronquée : dimension et opérateur restent lisibles.
            Le libellé complet est disponible au survol (title). */}
        <span
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {valueLabel}
        </span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        title="Retirer ce filtre"
        aria-label="Retirer ce filtre"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          padding: 0,
          background: 'transparent',
          color: 'var(--text-muted)',
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          // La croix de retrait ne doit jamais être écrasée par la troncature.
          flexShrink: 0,
        }}
      >
        <X size={12} />
      </button>
    </div>
  );
}

// ---------- PopoverShell ----------

function PopoverShell({ anchor, onClose, children }) {
  const shellRef = useRef(null);

  useEffect(() => {
    function onDocPointer(e) {
      if (!shellRef.current) return;
      if (!shellRef.current.contains(e.target)) onClose();
    }
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Position : juste sous le trigger. Si débordement à droite, on ancre à droite.
  const top = anchor ? anchor.bottom + 6 : 60;
  const left = anchor ? Math.min(anchor.left, window.innerWidth - 360) : 16;

  return (
    <div
      ref={shellRef}
      role="dialog"
      style={{
        position: 'fixed',
        top,
        left,
        width: 340,
        maxHeight: 'min(520px, calc(100vh - 80px))',
        background: 'var(--card-bg)',
        color: 'var(--text)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

// ---------- DimensionList ----------

function DimensionList({ scope, context, dimensions, onPick, onPickValue }) {
  const [search, setSearch] = useState('');
  // Ticket #78 — valeurs trouvées sur toutes les dimensions (min 2 caractères).
  const [valueGroups, setValueGroups] = useState([]);
  const [searching, setSearching] = useState(false);
  const term = search.trim();

  useEffect(() => {
    if (term.length < 2) {
      setValueGroups([]);
      setSearching(false);
      return undefined;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      apiFilter
        .search(scope, term, { affaireId: context?.affaireId })
        .then((res) => {
          if (!cancelled) setValueGroups(Array.isArray(res?.groups) ? res.groups : []);
        })
        .catch(() => {
          if (!cancelled) setValueGroups([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [scope, term, context?.affaireId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return dimensions;
    const q = search.trim().toLowerCase();
    return dimensions.filter((d) => d.label.toLowerCase().includes(q));
  }, [dimensions, search]);

  // Regroupe par catégorie.
  const grouped = useMemo(() => {
    const out = {};
    for (const d of filtered) {
      const cat = d.category ?? 'OTHER';
      if (!out[cat]) out[cat] = [];
      out[cat].push(d);
    }
    return out;
  }, [filtered]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 10px',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <Search size={14} style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            // Entrée = première valeur trouvée (saisie au clavier sans souris).
            const first = valueGroups[0];
            if (e.key === 'Enter' && first?.items?.[0]) {
              e.preventDefault();
              onPickValue(first.dimension, first.items[0]);
            }
          }}
          placeholder="Rechercher une personne, un client, une dimension…"
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text)',
            fontSize: 13,
          }}
        />
      </div>

      <div style={{ overflowY: 'auto', padding: 6, flex: 1 }}>
        {Object.entries(grouped).map(([cat, dims]) => (
          <div key={cat} style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                padding: '4px 8px',
              }}
            >
              {CATEGORY_LABELS[cat] ?? cat}
            </div>
            {dims.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => onPick(d)}
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 10px',
                  background: 'transparent',
                  color: 'var(--text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 13,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = 'var(--glass)')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <span>{d.label}</span>
                <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
              </button>
            ))}
          </div>
        ))}
        {valueGroups.map((group) => (
          <div key={`v:${group.dimension}`} style={{ marginBottom: 8 }}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                padding: '4px 8px',
              }}
            >
              {group.label}
            </div>
            {group.items.map((item) => (
              <button
                key={String(item.id)}
                type="button"
                onClick={() => onPickValue(group.dimension, item)}
                title={`Filtrer : ${group.label} est ${item.label}`}
                style={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  background: 'transparent',
                  color: 'var(--text)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  fontSize: 13,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--glass)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ flex: 1 }}>{item.label}</span>
              </button>
            ))}
          </div>
        ))}
        {searching && valueGroups.length === 0 && (
          <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-muted)' }}>
            Recherche…
          </div>
        )}
        {filtered.length === 0 && valueGroups.length === 0 && !searching && (
          <div
            style={{
              padding: '12px 10px',
              fontSize: 13,
              color: 'var(--text-muted)',
            }}
          >
            {term.length >= 2 ? 'Aucun résultat' : 'Aucune dimension'}
          </div>
        )}
      </div>
    </div>
  );
}

const CATEGORY_LABELS = {
  PEOPLE: 'Personnes',
  PROJECTS: 'Projets',
  ALLOCATIONS: 'Allocations',
  TIMEOFF: 'Congés',
  LOGTIME: 'Saisie',
};

// ---------- ValuePicker ----------

function ValuePicker({
  dimension,
  context,
  editingCondition,
  onLabelsLoaded,
  onBack,
  onCommit,
}) {
  const [operator, setOperator] = useState(editingCondition?.operator ?? 'is');
  const [selected, setSelected] = useState(() =>
    editingCondition ? [...editingCondition.values] : [],
  );
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!dimension) return undefined;
    // Source statique → on a déjà les valeurs inline.
    if (dimension.valueSource === 'static') {
      const all = dimension.staticValues ?? [];
      const filtered = debounced
        ? all.filter((v) => v.label.toLowerCase().includes(debounced.toLowerCase()))
        : all;
      setItems(filtered);
      return undefined;
    }
    // Source endpoint : fetch.
    let cancelled = false;
    setLoading(true);
    apiFilter
      .values(dimension.key, {
        q: debounced || undefined,
        limit: 50,
        extra: context ?? undefined,
      })
      .then((res) => {
        if (cancelled) return;
        const arr = Array.isArray(res?.items) ? res.items : [];
        setItems(arr);
        onLabelsLoaded?.(arr);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dimension, debounced, context, onLabelsLoaded]);

  if (!dimension) return null;

  function toggleValue(v) {
    setSelected((cur) => {
      const exists = cur.some((x) => sameValue(x, v));
      if (exists) return cur.filter((x) => !sameValue(x, v));
      return [...cur, v];
    });
  }

  function commit() {
    onCommit(operator, selected);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header : retour + nom + operator toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 10px',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Retour"
          title="Retour"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            background: 'transparent',
            color: 'var(--text-muted)',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <ChevronLeft size={16} />
        </button>
        <strong style={{ fontSize: 13, flex: 1 }}>{dimension.label}</strong>
        <div
          style={{
            display: 'inline-flex',
            background: 'var(--glass)',
            borderRadius: 'var(--radius-pill)',
            padding: 2,
          }}
        >
          <PillButton active={operator === 'is'} onClick={() => setOperator('is')}>
            est
          </PillButton>
          <PillButton
            active={operator === 'is_not'}
            onClick={() => setOperator('is_not')}
          >
            n’est pas
          </PillButton>
        </div>
      </div>

      {/* Search */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 10px',
          borderBottom: '1px solid var(--glass-border)',
        }}
      >
        <Search size={14} style={{ color: 'var(--text-muted)' }} />
        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={`Rechercher ${dimension.label.toLowerCase()}…`}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: 'var(--text)',
            fontSize: 13,
          }}
        />
      </div>

      {/* Liste des valeurs */}
      <ul
        role="listbox"
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 4,
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {loading && items.length === 0 && (
          <li style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 13 }}>
            Chargement…
          </li>
        )}
        {!loading && items.length === 0 && (
          <li style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 13 }}>
            Aucune valeur
          </li>
        )}
        {items.map((it) => {
          const isSelected = selected.some((x) => sameValue(x, it.id));
          return (
            <li
              key={String(it.id)}
              role="option"
              aria-selected={isSelected}
              onClick={() => toggleValue(it.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                background: isSelected ? 'var(--glass)' : 'transparent',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.background = 'var(--glass)';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.background = 'transparent';
              }}
            >
              <input
                type="checkbox"
                checked={isSelected}
                readOnly
                tabIndex={-1}
                style={{ pointerEvents: 'none' }}
              />
              <span style={{ flex: 1, fontSize: 13 }}>{it.label}</span>
            </li>
          );
        })}
      </ul>

      {/* Footer : compteur + Appliquer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 10px',
          borderTop: '1px solid var(--glass-border)',
          fontSize: 12,
          color: 'var(--text-muted)',
        }}
      >
        <span>
          {selected.length} sélectionné{selected.length > 1 ? 's' : ''}
        </span>
        <div style={{ display: 'inline-flex', gap: 6 }}>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => setSelected([])}
              style={{
                padding: '4px 8px',
                background: 'transparent',
                color: 'var(--text-muted)',
                border: 'none',
                fontSize: 12,
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Vider
            </button>
          )}
          <button
            type="button"
            onClick={commit}
            disabled={selected.length === 0 && !editingCondition}
            style={{
              padding: '4px 12px',
              background: 'var(--accent)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              fontWeight: 600,
              cursor: selected.length === 0 && !editingCondition ? 'not-allowed' : 'pointer',
              opacity: selected.length === 0 && !editingCondition ? 0.5 : 1,
            }}
          >
            Appliquer
          </button>
        </div>
      </div>
    </div>
  );
}

function PillButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '3px 10px',
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? '#fff' : 'var(--text-muted)',
        border: 'none',
        borderRadius: 'var(--radius-pill)',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function sameValue(a, b) {
  if (typeof a === typeof b) return a === b;
  // Tolère number/string mixés (cas enum int côté Float importé) — comparaison string.
  return String(a) === String(b);
}
